import { axiosClient } from '@/shared';
import {
  ApiResponse,
  AssetUploadPresignedResponseType,
  ErrorResponse,
} from '@/shared/types';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { z } from 'zod';
import { DtoExtension, DtoProviderType } from '@/shared/constants';
import {
  ContentInfoSchema,
  VideoContentType,
  ContentInfoType,
} from '../content/content-info-schema';

type AssetUploadPresignedApiResponse =
  ApiResponse<AssetUploadPresignedResponseType>;

/**
 * 프로젝트 귀속 업로드 정보
 *
 * - project가 없으면: 일반 에셋 (v-items, isAsset=true)
 * - project가 있고 isAsset=false: 프로젝트 임시 파일 (projects 버킷)
 * - project가 있고 isAsset=true: 에셋 전환 (v-items, 프로젝트 귀속 기록)
 */
export const ProjectUploadInfoSchema = z.object({
  /** 프로젝트 ID */
  id: z.number(),
  /** 에셋으로 저장 (true: v-items/영구, false: projects/임시) */
  isAsset: z.boolean().default(false),
  /** 프로젝트 콘텐츠 타입 코드 (미지정 시 UPLOADED) */
  projectContentType: z.number().optional(),
  /** 원본 콘텐츠 참조 ID (EDITED 등에서 사용) */
  referenceId: z.number().optional(),
});

export type ProjectUploadInfo = z.infer<typeof ProjectUploadInfoSchema>;

/**
 * Asset Upload Presigned URL 요청 스키마
 *
 * v2.0: ProjectContentInputDto.LocalFile과 동일한 스키마
 * - project 없이: MySpace에 에셋 업로드 (v-items)
 * - project 있고 isAsset=false: GTS 등 프로젝트 임시 파일 (projects)
 */
export const AssetUploadPresignedRequestSchema = z.object({
  type: z.literal('LOCAL_FILE'),
  name: z.string(),
  extension: z.nativeEnum(DtoExtension),
  size: z.number(),
  contentLevel: z.number().optional(),
  content: ContentInfoSchema,
  spaceId: z.number().optional(),
  providerType: z.nativeEnum(DtoProviderType).optional(),
  providerUrl: z.string().optional(),
  /** 프로젝트 귀속 정보 (없으면 일반 에셋 → v-items) */
  project: ProjectUploadInfoSchema.optional(),
});

// Backward compatibility aliases
export type AssetUploadVideoCreateDto = VideoContentType;
export type AssetUploadCreateInfoDto = ContentInfoType;
export type AssetUploadPresignedRequest = z.infer<
  typeof AssetUploadPresignedRequestSchema
>;

/**
 * v2.0 Asset Upload Presigned URL API
 *
 * POST /v2.0/asset/upload/pre-signed
 */
export const assetUploadPresignedApi = async (
  data: AssetUploadPresignedRequest,
) => {
  try {
    const response = await axiosClient.post<AssetUploadPresignedApiResponse>(
      '/v2.0/asset/upload/pre-signed',
      data,
    );

    return response.data;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const usePostAssetUploadPresignedApi = () => {
  return useMutation<
    AssetUploadPresignedApiResponse,
    AxiosError<ErrorResponse>,
    AssetUploadPresignedRequest
  >({
    mutationFn: assetUploadPresignedApi,
  });
};
